class DetailsModal extends HTMLElement {
  constructor() {
    super();
    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');
    this.dialog = this.querySelector('.search-modal.modal__content') || this.querySelector('[role="dialog"]');
    this.hiddenContentElements = [];
    this.summaryAccessibilityState = null;

    this.detailsContainer.addEventListener('keyup', (event) => event.code.toUpperCase() === 'ESCAPE' && this.close());
    this.summaryToggle.addEventListener('click', this.onSummaryClick.bind(this));
    this.querySelector('button[type="button"]').addEventListener('click', this.close.bind(this));

    this.summaryToggle.setAttribute('role', 'button');
    this.summaryToggle.setAttribute('aria-expanded', this.isOpen() ? 'true' : 'false');
    this.normalizeDialogSemantics();
  }

  normalizeDialogSemantics() {
    if (!this.dialog) return;
    this.dialog.setAttribute('role', 'region');
    this.dialog.removeAttribute('aria-hidden');
    this.dialog.removeAttribute('aria-modal');
  }

  isOpen() {
    return this.detailsContainer.hasAttribute('open');
  }

  onSummaryClick(event) {
    event.preventDefault();
    event.target.closest('details').hasAttribute('open') ? this.close() : this.open(event);
  }

  onBodyClick(event) {
    if (!this.contains(event.target) || event.target.classList.contains('modal-overlay')) this.close(false);
  }

  open(event) {
    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
    event.target.closest('details').setAttribute('open', true);
    this.summaryToggle.setAttribute('aria-expanded', 'true');
    this.setSummaryAccessibilityState(true);
    document.body.addEventListener('click', this.onBodyClickEvent);
    document.body.classList.add('overflow-hidden');
    this.setBackgroundContentHidden(true);

    trapFocus(
      this.detailsContainer.querySelector('[tabindex="-1"]'),
      this.detailsContainer.querySelector('input:not([type="hidden"])')
    );
  }

  close(focusToggle = true) {
    removeTrapFocus(focusToggle ? this.summaryToggle : null);
    this.detailsContainer.removeAttribute('open');
    this.summaryToggle.setAttribute('aria-expanded', 'false');
    this.setSummaryAccessibilityState(false);
    document.body.removeEventListener('click', this.onBodyClickEvent);
    document.body.classList.remove('overflow-hidden');
    this.setBackgroundContentHidden(false);
  }

  setSummaryAccessibilityState(isHidden) {
    if (!this.summaryToggle) return;

    if (isHidden) {
      this.summaryAccessibilityState = {
        hadHidden: this.summaryToggle.hasAttribute('hidden'),
        hadInert: this.summaryToggle.hasAttribute('inert'),
        hadTabindex: this.summaryToggle.hasAttribute('tabindex'),
        tabindex: this.summaryToggle.getAttribute('tabindex') || '',
      };

      this.summaryToggle.setAttribute('hidden', '');
      this.summaryToggle.setAttribute('inert', '');
      this.summaryToggle.setAttribute('tabindex', '-1');
      return;
    }

    if (!this.summaryAccessibilityState) return;

    const { hadHidden, hadInert, hadTabindex, tabindex } = this.summaryAccessibilityState;

    if (!hadHidden) {
      this.summaryToggle.removeAttribute('hidden');
    }

    if (!hadInert) {
      this.summaryToggle.removeAttribute('inert');
    }

    if (hadTabindex) {
      this.summaryToggle.setAttribute('tabindex', tabindex);
    } else {
      this.summaryToggle.removeAttribute('tabindex');
    }

    this.summaryAccessibilityState = null;
  }

  setBackgroundContentHidden(hide) {
    if (hide) {
      this.hiddenContentElements = [];

      document
        .querySelectorAll('.skip-to-content-link, #MainContent, footer, [id*="footer-group"]')
        .forEach((element) => this.hideElementFromAccessibilityTree(element));

      // Hide siblings around the active dialog up through the body branch.
      let currentElement = this.dialog || this.detailsContainer;
      while (currentElement && currentElement !== document.body) {
        const parent = currentElement.parentElement;
        if (!parent) break;

        Array.from(parent.children).forEach((sibling) => {
          if (sibling !== currentElement) this.hideElementFromAccessibilityTree(sibling);
        });

        currentElement = parent;
      }

      return;
    }

    this.hiddenContentElements.forEach(({ element, hadInert }) => {

      if (!hadInert) {
        element.removeAttribute('inert');
      }
    });

    this.hiddenContentElements = [];
  }

  hideElementFromAccessibilityTree(element) {
    if (!element || ['SCRIPT', 'STYLE', 'TEMPLATE'].includes(element.tagName)) return;
    if (this.hiddenContentElements.some((entry) => entry.element === element)) return;

    this.hiddenContentElements.push({
      element,
      hadInert: element.hasAttribute('inert'),
    });

    element.setAttribute('inert', '');
  }
}

customElements.define('details-modal', DetailsModal);
